import { ZodError, ZodSchema } from "zod";
import schemaRegistry from "../../logic/schemaRegistry.js";
import { isNumber } from "@bonusmap/shared_ui/library";
import { DataAmbitoComponent } from "./data-ambito-component.js";
import { DataBaseComponent } from "./data-base-component.js";
import { DataBonusParamComponent } from "./data-bonusparam-component.js";
import { DataBooleanComponent } from "./data-boolean-component.js";
import { DataContextComponent } from "./data-context-component.js";
import { DataDateComponent } from "./data-date-component.js";
import { DataDatetimeComponent } from "./data-datetime-component.js";
import { DataFilesComponent } from "./data-files-component.js";
import { DataLinksComponent } from "./data-links-component.js";
import { DataClinksComponent } from "./data-clinks-component.js";
import { DataNumberComponent } from "./data-number-template.js";
import { DataSlot } from "./data-slot.js";
import { DataStringComponent } from "./data-string-component.js";
import { DataTextareaComponent } from "./data-textarea-component.js";
import { toastError, toastSuccess } from "../../toasts.js";
import { DataOrganizationComponent } from "./data-organization.js";
import { DataToolbar } from "./data-toolbar.js";
import { ZodErrorComponent } from "../zod-error/zod-error.js";
import { ApiResponse, BonusDefRow, BonusDefRowNoId, BonusDefRowView } from "@bonusmap/api-types";
import { FrameComponent } from "../frames/frame-component.js";
import { DataRemove } from "./data-remove.js";
import { DataCategoriesComponent } from "./data-categories-component.js";
import { DataComuneComponent } from "./data-comune-component.js";
import { DataOrganizationsComponent } from "./data-organizations.js";
import { DataProfilesAttributesComponent } from "./data-profiles-attributes.js";
import { DataProfilesAdditionalComponent } from "./data-profiles-additional.js";
import { DataPermissionComponent } from "./data-permission-component.js";
import { registerCollectorExtensions } from "./collector-extensions/index.js";
import { CollectorExtensionInterface } from "./collector-extensions/collector-extension-base.js";
import { DataErogantiComponent } from "./data-eroganti-component.js";

export class DataCollector extends DataContextComponent {
    static webComponentName: string = "data-base-collector";
    protected _templateId: string = ""; // No template needed
    static observedAttributes: string[] = ["schema"];
    private _schema: ZodSchema<any> | null = null;
    get frameController(): FrameComponent | null {
        return window.frameController || null;
    }
    get toastError(): typeof toastError {
        if (this.frameController) return this.frameController.toastError;
        return toastError;
    }
    get toastSuccess(): typeof toastSuccess {
        if (this.frameController) return this.frameController.toastSuccess;
        return toastSuccess;
    }
    get schema(): ZodSchema<any> | null {
        if (!this._schema) {
            let schemaKey = (this.getAttribute('schema') || '').toLowerCase();
            if (this.action === "add" && schemaKey) {
                schemaKey += "-no-id";
            }
            if (schemaKey && schemaRegistry && schemaKey in schemaRegistry) {
                this._schema = schemaRegistry[schemaKey];
            } else {
                console.warn(`DataCollector: No schema found for key "${schemaKey}"`);
            }
        }
        return this._schema;
    }
    get apiName(): string {
        let schemaKey = (this.getAttribute('schema') || '').toLowerCase();
        return schemaKey;
    }
    private frameClose(): void {
        if (this.frameController) {
            this.frameController.actionExecuted = true;
            this.frameController.close();
        }
    }
    public get rowId(): number {
        const attrVal = this.getAttribute(`data-id`);
        if (attrVal) {
            const valNum = parseInt(attrVal);
            if (isNumber(valNum)) {
                return valNum;
            }
        }
        return 0
    }
    private get zodErrorComponent(): ZodErrorComponent | null {
        return document.querySelector('zod-error');
    }
    private raiseZodError(error: ZodError<any> | null): void {
        const zodComp = this.zodErrorComponent;
        if (zodComp) {
            zodComp.error = error;
        }
        else {
            console.error("DataCollector: ZodErrorComponent not found in DOM to display errors.");
        }
    }

    constructor() {
        super();
    }

    /**
     * Raccoglie i dati correnti da tutti i data-slot figli
     * @returns Record<string, any> - Oggetto con chiave = key del slot, valore = value del componente
     */
    public collectData(): Record<string, any> {
        const data: Record<string, any> = {};
        const slots = this.getAllDataSlots();

        slots.forEach(slot => {
            if (!slot.key) {
                console.warn('DataCollector: Found data-slot without key, skipping');
                return;
            }
            const dataComponent = this.getDataComponentFromSlot(slot);
            if (dataComponent) {
                data[slot.key] = dataComponent.value;
            } else {
                console.warn(`DataCollector: No data component found in slot with key "${slot.key}"`);
                data[slot.key] = null;
            }
        });

        return data;
    }

    /**
     * Ottiene tutti i data-slot figli diretti di questo collector
     * @returns DataSlot[] - Array di data-slot
     */
    private getAllDataSlots(): DataSlot[] {
        return Array.from(this.querySelectorAll('data-slot')) as DataSlot[];
    }

    /**
     * Ottiene il componente data-* contenuto in un data-slot
     * @param slot - Il data-slot da cui estrarre il componente
     * @returns DataBaseComponent | null - Il componente data-* o null se non trovato
     */
    private getDataComponentFromSlot(slot: DataSlot): DataBaseComponent | null {
        // Cerca il primo elemento che estende DataBaseComponent
        const dataElements = Array.from(slot.children).filter(child =>
            child.tagName.startsWith('DATA-')
        ) as DataBaseComponent[];
        return dataElements.length > 0 ? dataElements[0] : null;
    }

    /**
     * Imposta un valore specifico in un data-slot
     * @param key - Chiave del campo da modificare
     * @param value - Nuovo valore da impostare
     */
    public setFieldValue(key: string, value: any): void {
        const slot = this.getDataSlotByKey(key);
        if (!slot) {
            console.warn(`DataCollector: No data-slot found with key "${key}"`);
            return;
        }

        const dataComponent = this.getDataComponentFromSlot(slot);
        if (dataComponent) {
            dataComponent.value = value;
        } else {
            console.warn(`DataCollector: No data component found in slot with key "${key}"`);
        }
    }

    /**
     * Ottiene un data-slot specifico tramite la sua key
     * @param key - La chiave del data-slot da trovare
     * @returns DataSlot | null - Il data-slot trovato o null
     */
    private getDataSlotByKey(key: string): DataSlot | null {
        return this.querySelector(`data-slot[key="${key}"]`) as DataSlot;
    }

    /**
     * Ottiene il valore corrente di un campo specifico
     * @param key - Chiave del campo da leggere
     * @returns any - Il valore del campo o null se non trovato
     */
    public getFieldValue(key: string): any {
        const slot = this.getDataSlotByKey(key);
        if (!slot) {
            console.warn(`DataCollector: No data-slot found with key "${key}"`);
            return null;
        }

        const dataComponent = this.getDataComponentFromSlot(slot);
        return dataComponent ? dataComponent.value : null;
    }
    private _collectorExtension!: CollectorExtensionInterface;
    public get collectorExtension(): CollectorExtensionInterface {
        return this._collectorExtension;
    }
    public set collectorExtension(value: CollectorExtensionInterface) {
        this._collectorExtension = value;
    }
    /**
     * Setup event listeners per gestire il submit dei dati
     */
    protected setupEventListeners(): void {
        this.setElementRoleHandler('submit', this.handleSubmit.bind(this));
        window['dataCollector'] = this;
        // emetto un evento 'data-collector-ready' su document 
        // passando il collector come dettaglio dell'evento 
        // in modo che altre parti dell'app possano modificare la logica
    }
    /**
     * Analizza e valida i dati raccolti con lo schema Zod
     * @param collectedData - I dati da validare
     * @param showToast - Quando mostrare il toast: 'error' (solo errori), 'always' (sempre), 'never' (mai)
     * @returns Il risultato della validazione Zod (success/error) o null se schema non disponibile
     */
    analyzeData(collectedData: Record<string, any>, showToast: 'error' | 'always' | 'never' = 'error') {
        if (!this.schema) {
            if (showToast !== 'never') {
                this.toastError("Nessuno schema di validazione configurato");
            }
            return null;
        }

        const result = this.schema.safeParse(collectedData);

        if (result.success) {
            if (showToast === 'always') {
                this.toastSuccess("Validazione completata: tutti i dati sono corretti");
            }
            this.raiseZodError(null); // Pulisce eventuali errori precedenti
        } else {
            // Costruisce un messaggio esplicativo dagli errori Zod
            const errorMessages = result.error.issues.map(issue => {
                const path = issue.path.length > 0 ? issue.path.join('.') : 'root';
                return `• ${path}: ${issue.message}`;
            });
            const errorSummary = `Validazione fallita (${result.error.issues.length} errori):\n${errorMessages.join('\n')}`;

            console.error('Validation errors:', result.error.issues);
            if (showToast !== 'never') {
                this.toastError(errorSummary);
            }
            this.raiseZodError(result.error);
        }

        return result;
    }

    dryRun() {
        let collectedData = this.collectData();
        console.log(`i dati raccolti in origine sono:`, collectedData);
        const collectDataEvent = new CustomEvent('data-collect', { detail: { data: collectedData } });
        document.dispatchEvent(collectDataEvent);
        // l'evento potrebbe aver modificato i dati
        collectedData = collectDataEvent.detail.data;
        console.log('DataCollector: Collected data:', collectedData);

        this.analyzeData(collectedData, 'always');
    }
    /**
     * Gestisce il submit dei dati raccolti
     * @param event - L'evento di submit
     */
    private async handleSubmit(event: Event): Promise<void> {
        event.preventDefault();

        let collectedData = this.collectData();
        console.log(`i dati raccolti in origine sono:`, collectedData);
        const collectDataEvent = new CustomEvent('data-collect', { detail: { data: collectedData } });
        document.dispatchEvent(collectDataEvent);
        // l'evento potrebbe aver modificato i dati
        collectedData = collectDataEvent.detail.data;
        console.log('DataCollector: Collected data:', collectedData);

        // collectedData['id'] = ""; // Aggiungiamo l'id se esiste
        try {
            const result = this.schema?.safeParse(collectedData); // Validazione con Zod, lancia errore se non valido
            if (!result || !result.success) {
                this.raiseZodError(result?.error || null);
                return;
            }
        } catch (error) {
            this.toastError("Errore di validazione dei dati, controlla i campi inseriti.");
            console.error('DataCollector: Validation error:', error);
            return;
        }

        // ricordati che qui potremmo essere in una iframe, quindi dobbiamo fare dispatch di un evento
        this.dispatch('data-submit', {
            rowId: this.rowId,
            data: collectedData,
            action: this.action
        });

        // Opzionalmente, se è disponibile un'API diretta per il salvataggio
        // debolmente tipizzata per evitare dipendenze strette
        const api: any = (window as any).api?.manager?.[this.apiName];
        if (api) {
            try {
                const isAddAction = this.action === 'add';
                let result: ApiResponse<any> | null = null;
                if (isAddAction && 'add' in api) {
                    console.log('DataCollector: Adding new data:', collectedData);
                    result = await api.add(collectedData);
                    console.log('DataCollector: Add result:', result);
                } else if (!isAddAction && 'update' in api) {
                    result = await api.update(collectedData);
                }
                else {
                    result = null;
                }
                if (result && result.success) {
                    console.log('DataCollector: Data saved successfully');
                    this.toastSuccess("Dati salvati con successo.");
                    this.dispatch('data-saved', { rowId: this.rowId, data: collectedData });
                    this.frameClose(); // Chiudi il frame se tutto è andato a buon fine
                } else {
                    this.toastError("Errore nel salvataggio dei dati.");
                    console.error('DataCollector: Failed to save data:');
                    this.dispatch('data-save-error', { rowId: this.rowId, error: 'DataCollector: Failed to save data:' });
                }
            } catch (error) {
                console.error('DataCollector: Error during save:', error);
                this.toastError("Errore nel salvataggio dei dati.");
                this.dispatch('data-save-error', { rowId: this.rowId, error });
            }
        }
    }

    protected onConnected(): void {
        super.onConnected();
        if (this.schema === null) {
            this.innerHTML = `<div style="color:red;">DataCollector: No valid schema set. Please set a valid 'schema' attribute.</div>`;
            return;
        }
        this.setupEventListeners();
    }

    protected render(): void {
        // No template is needed for this component
        // This component serves as a base for other data collectors
    }
    static registerElement(): void {
        super.registerElement();
        registerCollectorExtensions();
        DataToolbar.registerElement();
        DataRemove.registerElement();
        // Register data components
        DataStringComponent.registerElement();
        DataPermissionComponent.registerElement();
        DataNumberComponent.registerElement();
        DataBooleanComponent.registerElement();
        DataDateComponent.registerElement();
        DataDatetimeComponent.registerElement();
        DataTextareaComponent.registerElement();
        DataOrganizationComponent.registerElement();
        DataOrganizationsComponent.registerElement();
        DataLinksComponent.registerElement();
        DataClinksComponent.registerElement();
        DataAmbitoComponent.registerElement();
        DataComuneComponent.registerElement();
        DataCategoriesComponent.registerElement();
        DataErogantiComponent.registerElement();
        DataBonusParamComponent.registerElement();
        DataFilesComponent.registerElement();
        DataProfilesAttributesComponent.registerElement();
        DataProfilesAdditionalComponent.registerElement();

        // Register Slot component
        DataSlot.registerElement(); // questo deve stare dopo gli altri componenti

    }
}